import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import './Legal.css';

function LegalLayout({ title, lastUpdated, children }) {
  const navigate = useNavigate();

  return (
    <div className="legal-page">
      <div className="legal-frame">
        <div className="frame-border"></div>

        <nav className="legal-nav">
          <div className="nav-left">
            <button className="nav-logo" onClick={() => navigate('/')} aria-label="Go to homepage">
              <img src="/akakai-logo.png" alt="akakAI" className="nav-brand-logo" />
            </button>
          </div>
          <div className="nav-center">
            <span className="nav-title">{title}</span>
          </div>
          <div className="nav-right">
            <button className="nav-btn nav-btn-ghost" onClick={() => navigate('/')}>
              <ArrowLeft size={14} />
              <span>Back</span>
            </button>
          </div>
        </nav>

        <div className="legal-scroll">
          <article className="legal-article">
            <h1>{title}</h1>
            <p className="legal-updated">Last updated: {lastUpdated}</p>
            <div className="legal-body">
              {children}
            </div>
          </article>
        </div>
      </div>
    </div>
  );
}

export function PrivacyPolicy() {
  return (
    <LegalLayout title="Privacy Policy" lastUpdated="July 23, 2026">
      <section>
        <h2>1. Introduction</h2>
        <p>Aegent, operated by akakAI ("we", "us", "our"), is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use the Aegent platform, including our website, application, and related services (collectively, the "Service").</p>
        <p>By accessing or using the Service, you agree to this Privacy Policy. If you do not agree, please discontinue use of the Service immediately.</p>
      </section>

      <section>
        <h2>2. Information We Collect</h2>
        <h3>2.1 Account Information</h3>
        <p>When you create an account, we collect your name, email address, and authentication credentials through Firebase Authentication. If you sign in with a third-party provider (such as Google), we receive your profile information from that provider.</p>

        <h3>2.2 Agent Configuration Data</h3>
        <p>We store the agent configurations you create, including agent names, identities, goals, business rules, escalation rules, listener configurations, installed skills, and related settings. This data is stored in PostgreSQL and is necessary to operate the Service.</p>

        <h3>2.3 Usage Data</h3>
        <p>We track usage for billing purposes — including the number of agent events and the credits consumed by optional features such as Aegent Intelligence. This data is aggregated per billing month and associated with your user account.</p>

        <h3>2.4 Log Data</h3>
        <p>The Service generates operational logs including agent execution logs, listener activity, skill execution results, and error reports. These logs are stored to help you monitor and debug your agents.</p>

        <h3>2.5 Community Contributions</h3>
        <p>If you submit listener scripts to the Community Library, the script content, name, description, and your user identifier are stored and made available to other users.</p>

        <h3>2.6 Technical Data</h3>
        <p>We automatically collect certain technical information when you access the Service, including your IP address, browser type, device information, and access timestamps.</p>
      </section>

      <section>
        <h2>3. How We Use Your Information</h2>
        <p>We use the information we collect to:</p>
        <ul>
          <li>Provide, operate, and maintain the Service</li>
          <li>Process and execute your agent configurations and automations</li>
          <li>Track usage for billing and subscription management</li>
          <li>Improve, personalize, and expand the Service</li>
          <li>Communicate with you about your account, updates, and support</li>
          <li>Detect and prevent fraud, abuse, and security incidents</li>
          <li>Comply with legal obligations</li>
        </ul>
      </section>

      <section>
        <h2>4. Data Processing by AI Models</h2>
        <p>The Service uses Anthropic's API (Claude) to power agent reasoning. When your agent processes events, relevant context (including event data, conversation history, and agent configuration) is sent to Anthropic for processing. We do not use your data to train AI models. Anthropic's data processing is governed by their own data processing agreement and privacy policy.</p>
      </section>

      <section>
        <h2>5. Aegent Intelligence (Optional Feature)</h2>
        <p><strong>Aegent Intelligence is an optional feature that you choose to enable and use.</strong> If you do not use it, this section does not apply to you. Aegent Intelligence powers certain AI-driven interactions — for example, holding a live phone conversation on your behalf.</p>

        <h3>5.1 What We Process</h3>
        <p>When you use an Aegent Intelligence capability, we process the inputs involved in the interaction and the outputs it generates. For voice features, this includes call audio and the resulting transcript or summary, along with operational metadata such as timestamps and duration used to operate, secure, and bill the feature.</p>

        <h3>5.2 How It Is Processed</h3>
        <p>To deliver these capabilities, the relevant inputs are routed to trusted third-party AI providers, including Google, which process them to generate a response on our behalf. The specific models and providers used may change over time as we improve the Service. We do not use your data to train AI models.</p>

        <h3>5.3 Telephony</h3>
        <p>Calls are placed and received through the calling provider you configure in your own account. That provider processes call information under its own terms, and you are responsible for maintaining that account.</p>

        <h3>5.4 Your Responsibilities</h3>
        <p>You are responsible for using Aegent Intelligence lawfully. In particular, for calls you must obtain any consent, and provide any notice, required by law — for example, notice that a call may be recorded or that a participant is interacting with an automated system — before the interaction. Where a disclosure message is configured, it is provided at the start of the interaction, but you remain responsible for meeting the requirements that apply to you.</p>

        <h3>5.5 Retention</h3>
        <p>Interaction content and outputs, such as transcripts and summaries, are retained only as needed to provide and operate the feature or as configured in your workspace, and are then deleted or de-identified.</p>
      </section>

      <section>
        <h2>6. Data Storage and Security</h2>
        <p>Your data is stored using Firebase (Google Cloud Platform) infrastructure. We implement industry-standard security measures including:</p>
        <ul>
          <li>Encryption in transit (TLS/SSL) for all communications</li>
          <li>Encryption at rest for stored data</li>
          <li>Firebase Security Rules to restrict data access</li>
          <li>Authentication-based access controls</li>
          <li>Regular security reviews and monitoring</li>
        </ul>
        <p>While we take reasonable measures to protect your information, no method of electronic transmission or storage is 100% secure. We cannot guarantee absolute security.</p>
      </section>

      <section>
        <h2>7. Data Sharing and Disclosure</h2>
        <p>We do not sell your personal information. We may share your information in the following circumstances:</p>
        <ul>
          <li><strong>Service Providers:</strong> We use Anthropic (Claude) to operate the Service. This provider processes data on our behalf under its terms.</li>
          <li><strong>Community Library:</strong> Scripts you voluntarily submit to the Community Library are shared with other users of the Service.</li>
          <li><strong>Legal Requirements:</strong> We may disclose information if required by law, regulation, legal process, or governmental request.</li>
          <li><strong>Protection of Rights:</strong> We may disclose information to protect the rights, property, or safety of akakAI, our users, or the public.</li>
        </ul>
      </section>

      <section>
        <h2>8. Data Retention</h2>
        <p>We retain your account data for as long as your account is active. Agent logs, knowledge base entries, and conversation histories are retained according to your plan limits. Usage tracking data is retained for billing purposes. You may request deletion of your account and associated data by contacting us.</p>
      </section>

      <section>
        <h2>9. Your Rights</h2>
        <p>Depending on your jurisdiction, you may have the right to:</p>
        <ul>
          <li>Access the personal data we hold about you</li>
          <li>Request correction of inaccurate data</li>
          <li>Request deletion of your data</li>
          <li>Object to or restrict certain processing</li>
          <li>Data portability</li>
          <li>Withdraw consent where processing is based on consent</li>
        </ul>
        <p>To exercise any of these rights, please contact us at the address provided below.</p>
      </section>

      <section>
        <h2>10. International Data Transfers</h2>
        <p>Your data may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place for any international transfers in compliance with applicable data protection laws.</p>
      </section>

      <section>
        <h2>11. Children's Privacy</h2>
        <p>The Service is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected data from a child, we will take steps to delete it.</p>
      </section>

      <section>
        <h2>12. Changes to This Policy</h2>
        <p>We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the updated policy on the Service and updating the "Last updated" date. Your continued use of the Service after any changes constitutes acceptance of the revised policy.</p>
      </section>

      <section>
        <h2>13. Contact Us</h2>
        <p>If you have questions about this Privacy Policy or our data practices, please contact us at:</p>
        <div className="legal-contact">
          <p><strong>akakAI</strong></p>
          <p><a href="https://akakai.com/" target="_blank" rel="noopener noreferrer">akakai.com</a></p>
        </div>
      </section>
    </LegalLayout>
  );
}

export function TermsOfService() {
  return (
    <LegalLayout title="Terms of Service" lastUpdated="July 23, 2026">
      <section>
        <h2>1. Acceptance of Terms</h2>
        <p>These Terms of Service ("Terms") govern your access to and use of the Aegent platform and related services (the "Service"), operated by akakAI ("we", "us", "our"). By creating an account or using the Service, you agree to be bound by these Terms. If you do not agree, do not use the Service.</p>
      </section>

      <section>
        <h2>2. Description of Service</h2>
        <p>Aegent is an autonomous agent platform that allows you to create, configure, and deploy AI-powered agents. The Service includes agent creation and management, listener and skill configuration, AI-powered code generation, a community library, and monitoring tools. The Service is provided on a subscription basis with usage-based billing.</p>
      </section>

      <section>
        <h2>3. Account Registration</h2>
        <p>To use the Service, you must create an account. You agree to:</p>
        <ul>
          <li>Provide accurate and complete registration information</li>
          <li>Maintain the security of your account credentials</li>
          <li>Promptly update your account information if it changes</li>
          <li>Accept responsibility for all activity that occurs under your account</li>
        </ul>
        <p>You must be at least 18 years of age to create an account and use the Service.</p>
      </section>

      <section>
        <h2>4. Subscriptions and Billing</h2>
        <h3>4.1 Plans</h3>
        <p>The Service offers subscription plans with usage-based billing, as described on our pricing page. Each paid plan includes a monthly allowance of credits and access to all features; plans differ in the number of credits included. Credits are consumed by agent activity and by optional features such as Aegent Intelligence. Building and configuring agents, and running compute-only subagents, do not consume credits.</p>

        <h3>4.2 Billing</h3>
        <p>Subscriptions are billed monthly through Stripe. By subscribing, you authorize us to charge your payment method on a recurring basis. Your monthly credit allowance resets at the beginning of each billing cycle, and unused credits do not roll over.</p>

        <h3>4.3 Usage Limits</h3>
        <p>If you use up your monthly credit allowance, agent activity will be paused until your credits reset or you upgrade your plan — unless you have enabled overage, in which case additional usage is charged per credit at the applicable overage rate. You will receive notice when approaching your limit.</p>

        <h3>4.4 Cancellation</h3>
        <p>You may cancel your subscription at any time. Cancellation takes effect at the end of the current billing period. No refunds are provided for partial billing periods.</p>
      </section>

      <section>
        <h2>5. User Content and Scripts</h2>
        <h3>5.1 Ownership</h3>
        <p>You retain ownership of all content you create on the Service, including agent configurations, listener scripts, installed skills, and business rules ("User Content").</p>

        <h3>5.2 License to Us</h3>
        <p>By using the Service, you grant us a limited, non-exclusive license to store, process, and transmit your User Content solely to operate and provide the Service.</p>

        <h3>5.3 Community Contributions</h3>
        <p>If you submit scripts to the Community Library, you grant all users of the Service a non-exclusive, royalty-free license to use, copy, and modify those scripts. You represent that you have the right to grant this license and that your submissions do not infringe any third-party rights.</p>

        <h3>5.4 Script Responsibility</h3>
        <p>You are solely responsible for the code you write, upload, or generate through the Service. This includes ensuring your scripts do not violate any laws, infringe intellectual property rights, or cause harm to third-party systems. We do not review or audit user scripts for compliance.</p>
      </section>

      <section>
        <h2>6. Acceptable Use</h2>
        <p>You agree not to use the Service to:</p>
        <ul>
          <li>Violate any applicable law, regulation, or third-party rights</li>
          <li>Send spam, phishing messages, or unsolicited communications</li>
          <li>Place automated, recorded, or AI-voice calls without each recipient's legally required consent, or in violation of applicable telemarketing, robocall, auto-dialing, do-not-call, or call-recording laws</li>
          <li>Distribute malware, viruses, or other harmful code</li>
          <li>Attempt to gain unauthorized access to other users' accounts or data</li>
          <li>Interfere with or disrupt the Service or its infrastructure</li>
          <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
          <li>Use the Service to build a competing product</li>
          <li>Exceed rate limits or abuse API endpoints</li>
          <li>Use the Service for any illegal, fraudulent, or deceptive purpose</li>
        </ul>
        <p>We reserve the right to suspend or terminate accounts that violate these terms without notice.</p>
      </section>

      <section>
        <h2>7. AI-Generated Content</h2>
        <p>The Service uses AI models to generate code, process events, and make decisions on behalf of your agents. You acknowledge that:</p>
        <ul>
          <li>AI-generated outputs may contain errors or inaccuracies</li>
          <li>You are responsible for reviewing and validating AI-generated code before deployment</li>
          <li>We are not liable for actions taken by your agents based on AI reasoning</li>
          <li>The evaluator provides an additional review layer but does not guarantee correctness</li>
        </ul>
      </section>

      <section>
        <h2>8. Aegent Intelligence and Automated Calling</h2>
        <p>Aegent Intelligence is an optional feature. If you enable or use it — including any capability that places, receives, records, or transcribes phone calls — you are solely responsible for using it in compliance with all applicable laws, and you represent and warrant that you will do so. In particular, you agree that:</p>
        <ul>
          <li>You will obtain all consents and provide all notices required by law before any call, including any consent required to record or transcribe a call and any disclosure that a participant is interacting with an automated or AI system;</li>
          <li>You have a lawful basis, and any necessary prior express consent, to contact each person you call, and you will comply with all applicable telemarketing, robocall, auto-dialing, do-not-call, and calling-time regulations (including, in the United States, the Telephone Consumer Protection Act and related rules);</li>
          <li>You will promptly honor opt-out and do-not-contact requests;</li>
          <li>You are responsible for your own telephony provider account and credentials, and for any charges that provider bills you; and</li>
          <li>You will not use the feature for unlawful, deceptive, harassing, or fraudulent communications.</li>
        </ul>
        <p>You are the party responsible for the calls you initiate or receive through the feature. Any disclosure message the feature provides at the start of an interaction is offered as a convenience and does not relieve you of your own compliance obligations. You agree to indemnify akakAI for any claims arising from your use of Aegent Intelligence, including your calling activity, as set out in the Indemnification section.</p>
      </section>

      <section>
        <h2>9. Third-Party Services</h2>
        <p>The Service integrates with third-party platforms through user-defined listeners and skills. We are not responsible for the availability, accuracy, or security of third-party services. Your use of third-party services is governed by their respective terms and policies. You are responsible for maintaining valid credentials and complying with the terms of any third-party services you connect to.</p>
      </section>

      <section>
        <h2>10. Intellectual Property</h2>
        <p>The Service, including its design, code, architecture, documentation, and branding, is owned by akakAI and protected by intellectual property laws. These Terms do not grant you any right to use our trademarks, logos, or brand elements without prior written consent.</p>
      </section>

      <section>
        <h2>11. Disclaimer of Warranties</h2>
        <p>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY. WE DISCLAIM ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.</p>
      </section>

      <section>
        <h2>12. Limitation of Liability</h2>
        <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, AKAKAI SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, BUSINESS OPPORTUNITIES, OR GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY FOR ANY CLAIMS ARISING UNDER THESE TERMS SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.</p>
      </section>

      <section>
        <h2>13. Indemnification</h2>
        <p>You agree to indemnify and hold harmless akakAI, its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including reasonable attorneys' fees) arising out of or related to your use of the Service, your User Content, your scripts and agent configurations, or your violation of these Terms.</p>
      </section>

      <section>
        <h2>14. Modifications to Terms</h2>
        <p>We may modify these Terms at any time. We will provide notice of material changes by posting the updated Terms on the Service and updating the "Last updated" date. Your continued use of the Service after changes take effect constitutes acceptance of the revised Terms.</p>
      </section>

      <section>
        <h2>15. Termination</h2>
        <p>We may suspend or terminate your access to the Service at any time, with or without cause, and with or without notice. Upon termination, your right to use the Service ceases immediately. Sections that by their nature should survive termination will survive, including ownership, warranty disclaimers, indemnification, and limitations of liability.</p>
      </section>

      <section>
        <h2>16. Governing Law</h2>
        <p>These Terms are governed by and construed in accordance with applicable law, without regard to conflict of law principles. Any disputes arising under these Terms shall be resolved through binding arbitration or in the courts of competent jurisdiction.</p>
      </section>

      <section>
        <h2>17. Contact</h2>
        <p>For questions about these Terms, please contact us at:</p>
        <div className="legal-contact">
          <p><strong>akakAI</strong></p>
          <p><a href="https://akakai.com/" target="_blank" rel="noopener noreferrer">akakai.com</a></p>
        </div>
      </section>
    </LegalLayout>
  );
}

export function CookiesPolicy() {
  return (
    <LegalLayout title="Cookies Policy" lastUpdated="April 3, 2026">
      <section>
        <h2>1. What Are Cookies</h2>
        <p>Cookies are small text files placed on your device when you visit a website. They are widely used to make websites work more efficiently and to provide information to site operators. This Cookies Policy explains how Aegent, operated by akakAI, uses cookies and similar technologies.</p>
      </section>

      <section>
        <h2>2. How We Use Cookies</h2>
        <p>We use cookies and similar technologies for the following purposes:</p>

        <h3>2.1 Essential Cookies</h3>
        <p>These cookies are strictly necessary for the Service to function. They include:</p>
        <ul>
          <li><strong>Authentication cookies:</strong> Managed by Firebase Authentication to maintain your login session and verify your identity across requests.</li>
          <li><strong>Security cookies:</strong> Used to detect and prevent fraudulent activity and protect your account.</li>
        </ul>
        <p>These cookies cannot be disabled as the Service would not function without them.</p>

        <h3>2.2 Functional Cookies</h3>
        <p>These cookies remember your preferences and settings to provide a personalized experience:</p>
        <ul>
          <li>UI preferences such as selected agent or active page state</li>
          <li>Session state for form inputs and workspace configurations</li>
        </ul>

        <h3>2.3 Analytics Cookies</h3>
        <p>We may use analytics tools to understand how users interact with the Service. These cookies collect aggregated, anonymized data about page views, feature usage, and navigation patterns. This data helps us improve the Service.</p>
      </section>

      <section>
        <h2>3. Third-Party Cookies</h2>
        <p>Some cookies are set by third-party services we use:</p>
        <ul>
          <li><strong>Firebase (Google):</strong> Authentication and session management cookies.</li>
          <li><strong>Stripe:</strong> Payment processing cookies set during subscription management and checkout flows. These are governed by Stripe's own cookie policy.</li>
        </ul>
        <p>We do not control these third-party cookies. Please refer to the respective third-party privacy and cookie policies for more information.</p>
      </section>

      <section>
        <h2>4. Local Storage and Session Storage</h2>
        <p>In addition to cookies, we use browser local storage and session storage to:</p>
        <ul>
          <li>Cache authentication tokens for faster page loads</li>
          <li>Store temporary form data to prevent loss during navigation</li>
          <li>Maintain application state across page refreshes</li>
        </ul>
        <p>These technologies function similarly to cookies but are stored differently in your browser. The same principles in this policy apply to their use.</p>
      </section>

      <section>
        <h2>5. Managing Cookies</h2>
        <p>Most web browsers allow you to control cookies through their settings. You can typically:</p>
        <ul>
          <li>View what cookies are stored on your device</li>
          <li>Delete individual or all cookies</li>
          <li>Block cookies from specific or all websites</li>
          <li>Set preferences for first-party vs. third-party cookies</li>
        </ul>
        <p>Please note that disabling essential cookies will prevent you from using the Service, as authentication and session management require them.</p>
        <p>For instructions on managing cookies in your browser:</p>
        <ul>
          <li><strong>Chrome:</strong> Settings &gt; Privacy and security &gt; Cookies</li>
          <li><strong>Firefox:</strong> Settings &gt; Privacy & Security &gt; Cookies</li>
          <li><strong>Safari:</strong> Preferences &gt; Privacy &gt; Cookies</li>
          <li><strong>Edge:</strong> Settings &gt; Cookies and site permissions</li>
        </ul>
      </section>

      <section>
        <h2>6. Do Not Track</h2>
        <p>Some browsers offer a "Do Not Track" (DNT) signal. The Service does not currently respond to DNT signals, as there is no industry-standard interpretation of this signal. We will update this policy if a standard is adopted.</p>
      </section>

      <section>
        <h2>7. Changes to This Policy</h2>
        <p>We may update this Cookies Policy from time to time. Changes will be posted on this page with an updated "Last updated" date. Your continued use of the Service after changes constitutes acceptance of the revised policy.</p>
      </section>

      <section>
        <h2>8. Contact</h2>
        <p>If you have questions about our use of cookies, please contact us at:</p>
        <div className="legal-contact">
          <p><strong>akakAI</strong></p>
          <p><a href="https://akakai.com/" target="_blank" rel="noopener noreferrer">akakai.com</a></p>
        </div>
      </section>
    </LegalLayout>
  );
}
